/**
 * What survives a reload for the previous person, dropped before the page
 * reloads for a new one (lib/accountChange.ts).
 *
 * A reload empties every in-memory store, but these are read back on the next
 * load: the saved user selection, the tenant store's chosen tenant and
 * workspace, the workspace hints in sessionStorage, the pinned cycle and the
 * chosen space. Left in place, the new person's first reads would go to the
 * previous person's workspace before their own memberships resolve. Only the
 * storage is cleared; the in-memory stores are left for the reload, so nothing
 * re-renders as nobody in the moment before the page unloads.
 */

import { forgetSavedUserSelection } from '../stores/prioritization/userStore';
import { useTenantStore } from '../stores/prioritization/tenantStore';
import { usePinnedCycleStore } from '../stores/prioritization/pinnedCycleStore';
import { useSpaceStore } from '../stores/prioritization/spaceStore';
import { setCurrentWorkspaceIdHint } from './supabaseSync';
import { clearTenantContext } from './tenantContext';

const TENANT_HINT_KEY = 'peakPriorities_currentTenantId';

export function forgetPreviousAccount(): void {
  forgetSavedUserSelection();
  void useTenantStore.persist.clearStorage();
  void usePinnedCycleStore.persist.clearStorage();
  void useSpaceStore.persist.clearStorage();
  setCurrentWorkspaceIdHint(null);
  try {
    sessionStorage.removeItem(TENANT_HINT_KEY);
  } catch {
    // storage blocked — nothing saved to clear
  }
  // The module-level context still names the previous person and workspace;
  // anything that reads it before the page unloads must find nothing.
  clearTenantContext();
}
